import type { Locale as LocaleString } from "@shared/types";
import { isDate, Locale } from "date-fns";
import { de, enUS as en, nl } from "date-fns/locale";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// Import client-shared i18n (initializes the instance)
import "@client-shared/lib/i18n";

// Import BCC translations
import deTranslation from "./locales/de/translation.json";
import enTranslation from "./locales/en/translation.json";
import nlTranslation from "./locales/nl/translation.json";
import { DateResource, formatIntlDate } from "./locales/utils/formatIntlDate";
import { formatIntlNumber } from "./locales/utils/formatIntlNumber";
import { formatIntlWeight } from "./locales/utils/formatIntlWeight";

interface Resources {
  [key: string]: {
    translation: {
      [key: string]: string | object;
    };
  };
}

export const resources: Resources = {
  en: { translation: enTranslation },
  de: { translation: deTranslation },
  nl: { translation: nlTranslation },
};

interface FallbackLng {
  [key: string]: [string];
}
const fallbackLng: FallbackLng = {
  de_DE: ["de"],
  en_US: ["en"],
  en_GB: ["en"],
  en_AU: ["en"],
  en_DE: ["en"],
  nl_NL: ["nl"],
  en_NL: ["en"],
  default: ["en"],
};

interface Locales {
  [key: string]: Locale;
}

const locales: Locales = { en, de, nl };

// Add BCC-specific translations to the existing i18n instance
// (client-shared has already initialized it)
if (i18n.isInitialized) {
  // Merge BCC translations with existing resources
  Object.keys(resources).forEach((lng) => {
    const resource = resources[lng];
    if (resource?.translation) {
      i18n.addResourceBundle(lng, "translation", resource.translation, true, true);
    }
  });
} else {
  // Fallback: initialize if client-shared hasn't (test environment)
  void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng,
      debug: false,
      defaultNS: "translation",
      ns: ["translation"],
      returnNull: false,
      interpolation: {
        format: (value, format, lng) => {
          if (!lng || !format) return;
          if (value && (isDate(value) || isDate(value.start))) {
            const locale = getLocale(locales, lng, fallbackLng);
            if (!locale) return value;
            const resource = getResource(resources, lng, fallbackLng);
            return formatIntlDate(value, format, locale, resource);
          } else if (format.includes("number")) {
            const locale = getLocale(locales, lng, fallbackLng);
            if (!locale) return value;
            return formatIntlNumber(value ?? 0, locale);
          } else if (format.includes("weight")) {
            const locale = getLocale(locales, lng, fallbackLng);
            if (!locale) return value;
            const resource = getResource(resources, lng, fallbackLng);
            return formatIntlWeight(value ?? 0, format, locale, resource, lng as LocaleString);
          }
          return value;
        },
        escapeValue: false,
      },
    });
}

export default i18n;

function getLocale(locales: Locales, lng: string, fallbackLng: FallbackLng) {
  return locales[lng] ?? (fallbackLng[lng] && locales[fallbackLng[lng][0]]) ?? locales["en"];
}
function getResource(resources: Resources, lng: string, fallbackLng: FallbackLng) {
  return (resources[lng] ?? (fallbackLng[lng] && resources[fallbackLng[lng][0]]) ?? resources["en"]) as DateResource;
}
